'use client';

import { useMemo, useCallback, useEffect, useState } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  ChatHarProvider,
  getToolConfig,
  defaultEditContentRenderer,
  Skeleton,
  type EditContentProps,
  type SignalData,
} from '@terra/ui-design-system';
import { useMainContentTabs } from '../../store/layoutStore';
import { hitlEditCallbackRegistry } from '../../store/hitlEditCallbackRegistry';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useHarById } from '../../hooks/api/useHarById';
import {
  buildSignalDataFromDto,
  mergeSignalHitlData,
  shouldHydrateSignalHitl,
} from '../../utils/signalHitlHydration';
import { useTargetScope, useAppTargetId } from '../../store/projectStore';
import { ChatFindingSearchWrapper } from '../chat/findings/ChatFindingSearchWrapper';
import { useApplicationById } from '../../hooks/api/useApplicationById';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import type { Tab } from '../../store/layoutStore';

type HitlEditTabProps = {
  tab: Tab;
};

export function HitlEditTab({ tab }: HitlEditTabProps) {
  const { closeTab } = useMainContentTabs();
  const { fetchHarById } = useHarById();
  const { targetId, targetType } = useTargetScope();
  const appTargetId = useAppTargetId();
  const { application, isLoading: isLoadingApplication } = useApplicationById({
    id: appTargetId,
  });
  const defaultCvssVersion =
    application?.cvssVersion === 'V3_1' ? '3.1' : '4.0';

  const handleFetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }): Promise<ToolExecutionHar[]> => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        return response?.hars || [];
      } catch (error) {
        console.error('[HitlEditTab] Error fetching HAR data:', error);

        return [];
      }
    },
    [],
  );

  const tabData = tab.data as {
    toolName: string;
    args: Record<string, unknown>;
    hitlMessageId: string;
    extractedData?: unknown[];
    threadId?: string;
  };

  const config = useMemo(
    () => getToolConfig(tabData.toolName),
    [tabData.toolName],
  );

  const baseExtractedData = useMemo(() => {
    if (tabData.extractedData) return tabData.extractedData;
    if (!tabData.args || !config.hitlConfig?.extractData) return [];

    return config.hitlConfig.extractData(tabData.args);
  }, [tabData.extractedData, tabData.args, config.hitlConfig]);

  const [extractedData, setExtractedData] =
    useState<unknown[]>(baseExtractedData);
  const [isHydratingSignal, setIsHydratingSignal] = useState(false);

  useEffect(() => {
    setExtractedData(baseExtractedData);
  }, [baseExtractedData]);

  useEffect(() => {
    if (
      !shouldHydrateSignalHitl({
        toolName: tabData.toolName,
        args: tabData.args,
      })
    ) {
      return;
    }

    const proposed =
      (baseExtractedData[0] as Partial<SignalData> | undefined) ??
      (tabData.args as Partial<SignalData>);
    const signalId = proposed.signalId;

    if (!signalId) {
      return;
    }

    let cancelled = false;

    setIsHydratingSignal(true);

    const hydrateSignal = async () => {
      try {
        const signal = await terraProxyApiClient.getSignal(signalId);

        if (cancelled) {
          return;
        }

        const merged = mergeSignalHitlData({
          proposed,
          existing: buildSignalDataFromDto(signal),
        });

        setExtractedData([merged]);
      } catch (error: unknown) {
        console.error('[HitlEditTab] Failed to hydrate signal for HITL edit', {
          error,
          signalId,
        });
      } finally {
        if (!cancelled) {
          setIsHydratingSignal(false);
        }
      }
    };

    void hydrateSignal();

    return () => {
      cancelled = true;
    };
  }, [baseExtractedData, tabData.args, tabData.toolName]);
  const handleClose = useCallback(() => {
    hitlEditCallbackRegistry.unregister(tabData.hitlMessageId);
    closeTab(tab.id);
  }, [tabData.hitlMessageId, closeTab, tab.id]);

  const handleSubmit = useCallback(
    (editedArgs: Record<string, unknown>) => {
      const callbacks = hitlEditCallbackRegistry.get(tabData.hitlMessageId);

      hitlEditCallbackRegistry.unregister(tabData.hitlMessageId);
      closeTab(tab.id);
      callbacks?.onEditComplete(editedArgs);
    },
    [tabData.hitlMessageId, closeTab, tab.id],
  );

  const handleApprove = useCallback(() => {
    const callbacks = hitlEditCallbackRegistry.get(tabData.hitlMessageId);

    if (!callbacks?.onApprove) {
      return;
    }

    hitlEditCallbackRegistry.unregister(tabData.hitlMessageId);
    closeTab(tab.id);
    callbacks.onApprove();
  }, [tabData.hitlMessageId, closeTab, tab.id]);

  const handleReject = useCallback(
    (message?: string) => {
      const callbacks = hitlEditCallbackRegistry.get(tabData.hitlMessageId);

      if (!callbacks?.onReject) {
        return;
      }

      hitlEditCallbackRegistry.unregister(tabData.hitlMessageId);
      closeTab(tab.id);
      callbacks.onReject(typeof message === 'string' ? message : undefined);
    },
    [tabData.hitlMessageId, closeTab, tab.id],
  );

  const handleDraftArgsChange = useCallback(
    (draftArgs: Record<string, unknown> | null) => {
      hitlEditCallbackRegistry.setDraftArgs(tabData.hitlMessageId, draftArgs);
    },
    [tabData.hitlMessageId],
  );

  const editContentProps: EditContentProps = {
    onClose: handleClose,
    onSubmit: handleSubmit,
    onApprove: handleApprove,
    onReject: handleReject,
    toolName: tabData.toolName,
    args: tabData.args,
    extractedData,
    defaultCvssVersion,
    onDraftArgsChange: handleDraftArgsChange,
  };

  const renderer = config.renderEditContent ?? defaultEditContentRenderer;
  const showLoading =
    isHydratingSignal || (!!appTargetId && isLoadingApplication);

  return (
    <ChatFindingSearchWrapper targetId={targetId} targetType={targetType}>
      <ChatHarProvider
        threadId={tabData.threadId ?? null}
        fetchHarData={handleFetchHarData}
        fetchHarById={fetchHarById}
      >
        <Box h="full" w="full" minW={0} overflow="hidden">
          {showLoading ? (
            <Box p="6">
              <Skeleton height="400px" width="100%" />
            </Box>
          ) : (
            renderer(editContentProps)
          )}
        </Box>
      </ChatHarProvider>
    </ChatFindingSearchWrapper>
  );
}
